import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, CloudUpload, ExternalLink, Loader2, TriangleAlert, X } from "lucide-react";
import { presentationApi } from "../../services/presentationApi";
import { ImagePicker } from "../Offers/proposal/SlideInspector";
import { formatDate } from "../Offers/EditorChrome";

const input = "w-full rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none disabled:bg-slate-50";
export const LEVEL_NOUNS = { client: "Client", programme: "Programme", project: "Projet" };

const Field = ({ label, children }) => (
  <label className="block text-xs font-medium text-slate-600">
    {label}
    <div className="mt-1">{children}</div>
  </label>
);

const Panel = ({ title, onClose, children }) => (
  <aside className="w-full shrink-0 space-y-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:w-80">
    <div className="flex items-center justify-between">
      <h2 className="font-semibold text-slate-900">{title}</h2>
      <button type="button" onClick={onClose} aria-label="Fermer" className="rounded p-1 text-slate-400 hover:bg-slate-100">
        <X className="h-4 w-4" />
      </button>
    </div>
    {children}
  </aside>
);

// ------------------------------------------------------------------------- Drive
// État du PDF sur le Drive, relu régulièrement : il part seul une minute et demie après la dernière modification.
export const useDriveStatus = (id, api = presentationApi) => {
  const [status, setStatus] = useState(null);
  const refresh = useCallback(async () => {
    if (!id) return;
    try { setStatus(await api.driveStatus(id)); } catch { /* état indisponible : rien à montrer */ }
  }, [id, api]);
  useEffect(() => {
    if (!id) return undefined;
    refresh();
    const timer = setInterval(() => { if (!window.document.hidden) refresh(); }, 30000);
    return () => clearInterval(timer);
  }, [id, refresh]);
  return { status, setStatus, refresh };
};

export const connectDrive = async () => {
  const result = await presentationApi.driveConnect(window.location.pathname + window.location.search);
  window.location.href = result.authorization_url || result.url;
};

// Pastille d'en-tête : « Drive à jour », « Drive : en attente », erreur ou « Relier le Drive ».
export const DriveChip = ({ drive, onOpen }) => {
  const status = drive.status;
  if (!status) return null;
  const base = "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs";
  if (status.state === "synced") {
    return (
      <a href={status.file_url} target="_blank" rel="noreferrer" className={`${base} bg-emerald-50 text-emerald-700 hover:underline`}
        title={`PDF déposé le ${formatDate(status.synced_at)}${status.account ? ` avec le compte de ${status.account}` : ""}`}>
        <CheckCircle2 className="h-3 w-3" /> Drive à jour
      </a>
    );
  }
  if (status.state === "error") {
    return (
      <button type="button" onClick={onOpen} className={`${base} bg-amber-50 text-amber-800`} title={status.error}>
        <TriangleAlert className="h-3 w-3" /> Drive : {status.can_write === false ? "à relier" : "dépôt en échec"}
      </button>
    );
  }
  if (status.state === "pending") {
    return (
      <button type="button" onClick={onOpen} className={`${base} bg-slate-100 text-slate-600`}
        title="Le PDF part sur le Drive une minute et demie après la dernière modification">
        <CloudUpload className="h-3 w-3" /> Drive : en attente
      </button>
    );
  }
  return null;
};

const DriveSection = ({ id, drive, onError, api = presentationApi }) => {
  const [busy, setBusy] = useState(false);
  const status = drive.status;
  const sync = async () => {
    setBusy(true);
    try { drive.setStatus(await api.driveSync(id)); } catch (error) { onError(error.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 border-t border-slate-100 pt-3">
      <h3 className="text-sm font-semibold text-slate-800">PDF sur le Drive</h3>
      <p className="text-xs text-slate-500">
        Après chaque modification, le PDF est réimprimé et remplace le précédent sur le Drive (le Drive garde
        l'historique), rangé dans « Deepflow / {api.doc === "report" ? "Rapports" : "Présentations"} / nature » du
        dossier du projet. Deepflow n'écrit que dans les fichiers qu'il a créés.
      </p>
      {status?.file_url && (
        <div className="flex flex-wrap gap-3 text-xs">
          <a href={status.file_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline">
            <ExternalLink className="h-3 w-3" /> Ouvrir le PDF
          </a>
          {status.folder_url && (
            <a href={status.folder_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline">
              <ExternalLink className="h-3 w-3" /> Dossier{status.folder_category && !status.folder_category.startsWith("(") ? ` « ${status.folder_category} »` : ""}
            </a>
          )}
        </div>
      )}
      {status?.synced_at && <p className="text-xs text-slate-500">Dernier dépôt : {formatDate(status.synced_at)}{status.account ? ` (compte de ${status.account})` : ""}.</p>}
      {status?.folder_kind === "deepflow" && (
        <p className="text-xs text-slate-500">
          Pas de dossier de projet accessible : le PDF est rangé dans « Deepflow » de votre Drive. Ce dossier peut être
          déplacé (dossier du projet, Drive partagé) : Deepflow continue d'y écrire.
        </p>
      )}
      {status?.state === "error" && <p className="text-xs text-amber-800">{status.error}</p>}
      <div className="flex flex-wrap gap-2">
        {status?.can_write === false && (
          <button type="button" onClick={() => connectDrive().catch((error) => onError(error.message))}
            className="rounded-full bg-slate-900 px-3 py-1 text-xs text-white">Autoriser l'écriture sur mon Drive</button>
        )}
        {status?.can_write && (
          <button type="button" disabled={busy} onClick={sync}
            className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CloudUpload className="h-3 w-3" />} Déposer maintenant
          </button>
        )}
      </div>
    </div>
  );
};

// --------------------------------------------------------------------- réglages
// Nature de la présentation : sous-dossier du Drive (Deepflow / Présentations / <nature>), lue par l'IA ou choisie ici.
const NatureField = ({ presentation, categories, editable, onCategory }) => (
  <Field label="Rangement sur le Drive (nature)">
    <select className={input} disabled={!editable} value={presentation.category_source === "manuel" ? presentation.category : ""}
      onChange={(event) => onCategory(event.target.value)}>
      <option value="">
        {presentation.category_source === "ia" ? `Automatique : ${presentation.category}`
          : "Automatique (l'IA la choisira quand le deck sera écrit)"}
      </option>
      {categories.map((name) => <option key={name} value={name}>{name}</option>)}
    </select>
  </Field>
);

export const PresentationSettings = ({
  id, data, meta, editable, onMeta, drive, onTemplateSaved, onCategory, onClientLogo, onError, onClose,
  api = presentationApi, children,
}) => {
  const client = data.presentation.scope?.client_name;
  const report = api.doc === "report";
  const keepLogo = async () => {
    try {
      await api.keepClientLogo(id, meta.client_logo);
      onClientLogo(meta.client_logo);
    } catch (error) { onError(error.message); }
  };
  const set = (field) => (event) => onMeta({ [field]: event.target.value || null }, `meta.${field}`);
  const [busy, setBusy] = useState("");
  const templates = data.template || [];
  const saveTemplate = async (level, clear) => {
    const noun = LEVEL_NOUNS[level].toLowerCase();
    const question = clear ? `Retirer le modèle du ${noun} ?`
      : report ? `Faire de ce rapport le modèle des prochains rapports du ${noun} ? Il remplacera le modèle actuel.`
        : `Faire de cette présentation le modèle des prochaines présentations du ${noun} ? Il remplacera le modèle actuel.`;
    if (!window.confirm(question)) return;
    setBusy(level);
    try { onTemplateSaved(await api.saveTemplate(id, level, clear)); } catch (error) { onError(error.message); } finally { setBusy(""); }
  };
  return (
    <Panel title={report ? "Réglages du rapport" : "Réglages de la présentation"} onClose={onClose}>
      <Field label="Nature du document (couverture et pied de page)">
        <input className={input} disabled={!editable} value={meta.kicker || ""} maxLength={120} onChange={set("kicker")}
          placeholder={report ? "Rapport d'essais, Rapport d'étude, Note technique…" : "Présentation, Point d'avancement, Restitution…"} />
      </Field>
      {children}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Référence">
          <input className={input} disabled={!editable} value={meta.reference || ""} maxLength={60} onChange={set("reference")} />
        </Field>
        <Field label="Version">
          <input className={input} disabled={!editable} value={meta.version || ""} maxLength={20} onChange={set("version")} />
        </Field>
        <Field label="Date">
          <input type="date" className={input} disabled={!editable} value={meta.date || ""} onChange={set("date")} />
        </Field>
      </div>
      <div className="space-y-1">
        <span className="text-xs font-medium text-slate-600">Logo du client (couverture)</span>
        {editable
          ? <ImagePicker value={meta.client_logo} label="Logo" onChange={(ref) => onMeta({ client_logo: ref })} />
          : <p className="text-xs text-slate-500">{meta.client_logo ? "Logo ajouté." : "Aucun logo."}</p>}
        {editable && client && meta.client_logo && meta.client_logo !== data.client_logo && (
          <button type="button" className="text-xs text-slate-600 underline" onClick={keepLogo}>
            Garder ce logo pour {client} (fiche client, offres et prochaines présentations)
          </button>
        )}
        {client && meta.client_logo && meta.client_logo === data.client_logo && (
          <p className="text-xs text-slate-500">Logo enregistré pour {client}.</p>
        )}
      </div>
      <DriveSection id={id} drive={drive} onError={onError} api={api} />
      <NatureField presentation={data.presentation} categories={data.categories || []} editable={editable}
        onCategory={onCategory} />
      <div className="space-y-2 border-t border-slate-100 pt-3">
        <h3 className="text-sm font-semibold text-slate-800">Modèles</h3>
        <p className="text-xs text-slate-500">
          {report ? "Un nouveau rapport" : "Une nouvelle présentation"} part du modèle le plus précis (projet, puis programme, puis client).
        </p>
        {templates.map((item) => (
          <div key={item.level} className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="text-slate-700">{LEVEL_NOUNS[item.level]} {item.name} : {item.presentation_id ? (item.presentation_id === id ? (report ? "ce rapport" : "cette présentation") : "un modèle") : "aucun"}</span>
            {editable && (
              <span className="flex gap-1">
                <button type="button" disabled={busy === item.level} onClick={() => saveTemplate(item.level, false)}
                  className="rounded-full border border-slate-300 px-2 py-0.5 text-slate-700 hover:bg-slate-50">En faire le modèle</button>
                {item.presentation_id && (
                  <button type="button" disabled={busy === item.level} onClick={() => saveTemplate(item.level, true)}
                    className="rounded-full px-2 py-0.5 text-slate-500 hover:bg-slate-100">Retirer</button>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
};

// --------------------------------------------------------------------- consignes
const SOURCES = { comment: "Commentaire", edit: "Retouche après Nexus", chat: "Remarque", agent: "Noté par Nexus" };

// Consignes tenues par l'IA d'après les retours : rien à gérer, on peut seulement en retirer une ou dire un retour.
export const GuidesPanel = ({ id, scope, editable, onError, onClose, api = presentationApi }) => {
  const [guides, setGuides] = useState(null);
  const [note, setNote] = useState("");
  const [sent, setSent] = useState(false);
  const refresh = useCallback(async () => {
    try { setGuides(await api.guides(scope.type, scope.id)); } catch (error) { onError(error.message); }
  }, [scope.type, scope.id, onError, api]);
  useEffect(() => { refresh(); }, [refresh]);

  const retire = async (rule) => {
    const reason = window.prompt(`Retirer la consigne « ${rule.text} » ? Pourquoi (facultatif) :`, "");
    if (reason === null) return;
    try { await api.retireRule(rule.id, reason || null); await refresh(); } catch (error) { onError(error.message); }
  };
  const send = async (event) => {
    event.preventDefault();
    if (!note.trim()) return;
    try {
      await api.addGuideNote(id, note.trim());
      setNote("");
      setSent(true);
      await refresh();
    } catch (error) { onError(error.message); }
  };

  return (
    <Panel title="Consignes" onClose={onClose}>
      <p className="text-xs text-slate-500">
        Tenues par l'IA d'après vos commentaires, vos retouches après Nexus et vos remarques : Nexus les suit pour les
        {api.doc === "report" ? " prochains rapports" : " prochaines présentations"}. Rien à faire de votre côté.
      </p>
      {!guides ? <p className="text-xs text-slate-500">Chargement…</p> : (
        <>
          {guides.levels.map((level) => (
            <section key={level.level} className="space-y-1">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{LEVEL_NOUNS[level.level]} · {level.name}</h3>
              {level.rules.length === 0 ? <p className="text-xs italic text-slate-400">Aucune consigne pour l'instant.</p> : (
                <ul className="space-y-1">
                  {level.rules.map((rule) => (
                    <li key={rule.id} className="group flex items-start gap-1 text-sm text-slate-700">
                      <span className="flex-1">{rule.text}</span>
                      {editable && (
                        <button type="button" onClick={() => retire(rule)} title="Retirer cette consigne"
                          className="rounded p-0.5 text-slate-300 opacity-0 hover:text-red-600 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
          {guides.pending_notes > 0 && (
            <p className="text-xs text-slate-500">{guides.pending_notes} retour{guides.pending_notes > 1 ? "s" : ""} en cours de lecture par l'IA.</p>
          )}
          {editable && (
            <form onSubmit={send} className="space-y-1 border-t border-slate-100 pt-3">
              <textarea className={input} rows={2} value={note} maxLength={2000} onChange={(event) => { setNote(event.target.value); setSent(false); }}
                placeholder="Un retour pour les prochaines fois (« ce client veut des schémas plutôt que du texte »)" />
              <div className="flex items-center justify-between">
                {sent ? <span className="text-xs text-emerald-700">Noté, l'IA s'en charge.</span> : <span />}
                <button type="submit" disabled={!note.trim()} className="rounded-full bg-slate-900 px-3 py-1 text-xs text-white disabled:opacity-40">Noter</button>
              </div>
            </form>
          )}
          {guides.notes?.length > 0 && (
            <details className="border-t border-slate-100 pt-3">
              <summary className="cursor-pointer text-xs font-semibold text-slate-600">Journal des retours</summary>
              <ul className="mt-2 space-y-2">
                {guides.notes.map((item) => (
                  <li key={item.id} className="text-xs text-slate-600">
                    <div className="text-slate-400">{SOURCES[item.source] || item.source}{item.author_name ? ` · ${item.author_name}` : ""} · {formatDate(item.created_at)}</div>
                    <div className="whitespace-pre-line">{item.body.length > 400 ? `${item.body.slice(0, 400)}…` : item.body}</div>
                    <div className="text-slate-400">{item.digested_at ? `→ ${item.outcome}` : "→ en attente de lecture"}</div>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </Panel>
  );
};
